import {
  createAssessmentDraft,
  updateAssessmentDraft,
} from '../../assessment-portal/services';
import type { AssessmentDraftPayload } from '../../assessment-portal/types/questionnaire';
import { RequestStatus } from '../../model';
import { ASSESSMENT_TYPE } from '../data/constants';
import type { AdhdFlowState, PatientInfoValues } from '../types/flow';
import { buildCarePathwayMetadata, buildConsentMetadata } from './build-consent-metadata';
import { readUtm, resolveSource } from './source-tracking';
import { safeGetSessionStorage } from './browser-storage';

/** Per tab, so a reload keeps the draft but a new tab starts a new booking. */
const STORAGE_KEY = 'adhd_draft_workflow_v1';

/**
 * The server no longer accepts changes to this draft, so the next sync makes
 * a new one. DRAFT_PATIENT_MISMATCH: the details now name another patient,
 * e.g. a second child booked in the same tab.
 */
const DRAFT_GONE_ERROR_CODES = ['WORKFLOW_NOT_FOUND', 'ALREADY_CONFIRMED', 'DRAFT_PATIENT_MISMATCH'];

let pendingSync: Promise<string | null> | null = null;
/** The draft of the booking in progress; sessionStorage only carries it across a reload. */
let currentDraftId: string | null = null;
/** Bumped when a booking finishes, so syncs started for it can no longer touch the draft. */
let generation = 0;

const readStoredDraftId = (): string | null => {
  const storage = safeGetSessionStorage();
  if (!storage) return null;
  try {
    return storage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

export const readDraftWorkflowId = (): string | null => currentDraftId || readStoredDraftId();

const rememberDraftWorkflowId = (workflowId: string): void => {
  currentDraftId = workflowId;
  const storage = safeGetSessionStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, workflowId);
  } catch {
    // best-effort: kept in memory, only a reload loses it
  }
};

const forgetDraftWorkflowId = (): void => {
  currentDraftId = null;
  const storage = safeGetSessionStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort; ignore storage errors
  }
};

/** Called once the booking is confirmed: the next booking in this tab starts afresh. */
export const clearDraftWorkflow = (): void => {
  generation += 1;
  pendingSync = null;
  forgetDraftWorkflowId();
};

export const buildDraftPayload = (
  flowState: AdhdFlowState,
  values: PatientInfoValues,
): AssessmentDraftPayload => {
  const address = {
    streetAddress: values.address,
    suburb: values.suburb,
    state: values.state,
    postcode: values.postcode,
  };
  const utm = readUtm();

  return {
    assessmentType: ASSESSMENT_TYPE,
    patientInfo: {
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth: values.dateOfBirth,
      gender: values.gender,
      mobile: values.mobile,
      email: values.email,
      addressResidential: address,
      addressPostal: address,
    },
    metadata: {
      cultural: values.cultural,
      referral: values.referral,
      consents: buildConsentMetadata(flowState),
      carePathway: buildCarePathwayMetadata(flowState),
      source: resolveSource(utm),
      ...(utm ? { utm } : {}),
    },
  };
};

const syncNow = async (
  payload: AssessmentDraftPayload,
  syncGeneration: number,
): Promise<string | null> => {
  // A sync queued or answered after the booking finished must not bring
  // its draft back for the next booking in this tab.
  const isCurrent = () => syncGeneration === generation;
  if (!isCurrent()) return null;

  const existingId = readDraftWorkflowId();
  if (existingId) {
    const updated = await updateAssessmentDraft(existingId, payload);
    if (updated.status === RequestStatus.SUCCESS) return existingId;
    // Any other failure leaves the draft in place: the booking can still confirm it.
    if (!DRAFT_GONE_ERROR_CODES.includes(updated.errorCode || '')) return existingId;
    if (!isCurrent()) return null;
    forgetDraftWorkflowId();
  }

  const created = await createAssessmentDraft(payload);
  if (created.status !== RequestStatus.SUCCESS || !isCurrent()) return null;
  rememberDraftWorkflowId(created.workflowId);
  return created.workflowId;
};

/**
 * Creates the draft booking, or updates the one this tab already made. Runs
 * in the background and never rejects: when it fails, the booking is created
 * in full on confirm. Syncs run one after another, so a second Next updates
 * the draft the first one created instead of making another.
 */
export const syncDraftWorkflow = (payload: AssessmentDraftPayload): Promise<string | null> => {
  const previous = pendingSync || Promise.resolve(null);
  const syncGeneration = generation;
  const next = previous
    .then(() => syncNow(payload, syncGeneration))
    .catch(() => readDraftWorkflowId());
  pendingSync = next;
  return next;
};

/** Longest confirm waits for a draft sync before booking without it. */
export const DRAFT_SYNC_WAIT_MS = 10000;

/**
 * The draft to confirm, once any sync still in flight has finished. A sync
 * that hangs is not waited for: the booking goes ahead with whatever draft
 * this tab already has, or is created in full.
 */
export const resolveDraftWorkflowId = async (): Promise<string | null> => {
  if (pendingSync) {
    let timer = 0;
    const timeout = new Promise<void>((resolve) => {
      timer = window.setTimeout(resolve, DRAFT_SYNC_WAIT_MS);
    });
    await Promise.race([pendingSync, timeout]);
    window.clearTimeout(timer);
  }
  return readDraftWorkflowId();
};
